import { css, Interpolation, Theme } from '@emotion/react'
import styled from '@emotion/styled'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import {
    CSSProperties,
    forwardRef,
    memo,
    MouseEventHandler,
    useCallback,
    useRef,
} from 'react'
import { setPinHelperID } from '../../app-state/state.rooms'
import { avatarUrl } from '../../mfcLogic/avatarPreviewUrl'
import { APPSTATE } from '../../state/state.app'
import { isTouchDevice } from '../../utilities/browser'
import {
    hideDragHelper,
    highlightDragHelper,
    showDragHelper,
} from '../presentation/Chat/Rooms/PinnedRooms/PinDragHelper'
import { palette } from '../styles/colors'
import { ripple } from '../styles/keyframes'
import shape from '../styles/shape'

/** display pixel ratio */
const DPR = 2

const disabledStyles = css({
    opacity: 0,
})

export const broadcastingStyles = css({
    boxShadow: `0 0 0 2px ${palette.background}`,
    backgroundColor: palette.avatarBadgeOnline,
    color: palette.avatarBadgeOnline,
    '::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: `${ripple} 1.2s infinite ease-in-out`,
        border: '1px solid currentColor',
        content: '""',
    },
})

const offlineStyles = css({
    boxShadow: `0 0 0 2px ${palette.background}`,
    backgroundColor: palette.avatarBadgeOffline,
})

const badgeStyles = css({
    boxShadow: `0 0 0 2px ${palette.background}`,
    backgroundColor: palette.avatarBadgeOnline,
    color: palette.avatarBadgeOnline,
})

const badgePointerStyles = css({ cursor: 'pointer' })

const badgeContainerStyles = css({
    display: 'inline-flex',
    position: 'relative',
    flexShrink: 0,
    verticalAlign: 'middle',
})

const avatarContainerStyles = css({
    position: 'relative',
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    userSelect: 'none',
    backgroundImage: palette.avatarFallbackUrl,
    backgroundSize: 'cover',
})

const avatarImageStyles = css({
    color: 'transparent',
    objectFit: 'cover',
    borderRadius: '50%',
    // cursor: 'grab',
    // clipPath: 'circle(50%)',
    transform: 'translate(0,0)',
    opacity: 1,
})

const dragHandleStyles = css({
    position: 'absolute',
    display: 'flex',
    left: 0,
    top: 0,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.4s ease-in-out',
    background: palette.avatarBadgeHover,
    '& svg': {
        opacity: 0.9,
        color: 'white',
    },
})

type DragHandleProps = {
    size: number | string
    onDragStart: any
    onDrag: any
    onDragEnd: any
}

const DragHandle = memo(
    forwardRef<HTMLElement, DragHandleProps>(
        ({ size, onDragStart, onDrag, onDragEnd }, ref) => {
            const handleSize = {
                width: size,
                height: size,
            }
            return (
                <span
                    ref={ref}
                    draggable
                    css={dragHandleStyles}
                    style={handleSize}
                    onDragStart={onDragStart}
                    onDrag={onDrag}
                    onDragEnd={onDragEnd}
                >
                    <DragIndicatorIcon data-testid="DragIndicatorIcon" />
                </span>
            )
        },
    ),
)

export const Dot = styled('div')({
    height: 8,
    minWidth: 8,
    padding: 0,
    borderRadius: shape.borderRadius,
    right: '14%',
    bottom: '14%',
    transform: 'scale(1) translate(50%, 50%)',
    transformOrigin: '100% 100%',
    display: 'flex',
    zIndex: 1,
    position: 'absolute' as const,
})

const getDotStyles = (
    offline: boolean,
    broadcasting: boolean,
    disableBadge: boolean,
) => {
    if (disableBadge) {
        return disabledStyles
    }
    if (broadcasting) {
        return broadcastingStyles
    }
    if (offline) {
        return offlineStyles
    }
    return badgeStyles
}

type BadgeAvatarProps = {
    alt?: string
    avatar?: string

    id?: number
    offline?: boolean
    broadcasting?: boolean

    size: number
    small?: boolean
    border?: boolean
    disableBadge?: boolean
    addBoxShadow?: boolean
    disableDrag?: boolean

    style?: CSSProperties
    avatarStyleOverrides?: Interpolation<Theme>
    className?: string

    onClick?: MouseEventHandler<HTMLSpanElement>
    title?: string
    onMouseEnter?: MouseEventHandler<HTMLSpanElement>
    onMouseLeave?: MouseEventHandler<HTMLSpanElement>
}

const BadgeAvatar = memo(
    forwardRef<HTMLElement, BadgeAvatarProps>((props, ref) => {
        const {
            alt,
            avatar,

            id,
            offline = false,
            broadcasting = false,

            size,
            small = false,
            border = false,
            disableBadge = false,
            addBoxShadow,
            disableDrag = false,

            style, // for cases where you dont need to override deeper components
            avatarStyleOverrides,
            className,

            onClick,
            title, // display as tooltip
            onMouseEnter,
            onMouseLeave,
        } = props

        const imageRef = useRef(null)
        const dragHandleRef = useRef(null)

        const avatarSize =
            small || size * DPR < 50
                ? 50
                : size * DPR < 100
                  ? 100
                  : size * DPR < 150
                    ? 150
                    : 300

        const avatarSrc = avatar ? avatar : avatarUrl(id, avatarSize)

        const dragStart = useCallback(
            (event) => {
                if (!broadcasting) {
                    setPinHelperID(0)
                    return
                }
                // console.log('[BadgeAvatar] dragStart', id)
                // 1) set up the drag image so we've got a consistent look
                event.dataTransfer.setData('text/plain', id)
                event.dataTransfer.setDragImage(imageRef.current, 22, 22)
                event.dataTransfer.effectAllowed = 'move'
                // 2) open/show the drag receiver area
                showDragHelper(id)
            },
            [id, broadcasting],
        )
        const dragMove = useCallback((event) => {
            const { clientX, clientY } = event
            highlightDragHelper(clientX, clientY)
        }, [])
        const dragEnd = useCallback(() => {
            hideDragHelper()
        }, [])

        const mouseEnter = useCallback(
            (event) => {
                // TODO: should also check if its a public show, not just broadcasting
                if (dragHandleRef.current && broadcasting) {
                    if (APPSTATE.app.isMobile || isTouchDevice) return

                    dragHandleRef.current.style.opacity = 1
                    // showDragHelper(id)
                }
                onMouseEnter?.(event)
            },
            [id, broadcasting, onMouseEnter],
        )
        const mouseLeave = useCallback(
            (event) => {
                if (dragHandleRef.current && broadcasting) {
                    dragHandleRef.current.style.opacity = 0
                    hideDragHelper()
                }
                onMouseLeave?.(event)
            },
            [broadcasting, onMouseLeave],
        )

        return (
            <span
                className={className}
                css={[badgeContainerStyles, onClick && badgePointerStyles]}
                style={style}
                ref={ref}
                onClick={onClick}
                title={title}
                onMouseEnter={mouseEnter}
                onMouseLeave={mouseLeave}
                id={`avatar-${id}`}
                data-testid="Container__BadgeAvatar"
            >
                <div
                    css={[avatarContainerStyles, avatarStyleOverrides]}
                    style={{
                        width: size,
                        height: size,
                        backgroundColor: addBoxShadow
                            ? palette.avatarFallbackBackground
                            : palette.avatarFallbackBackgroundGrey,
                        boxShadow: addBoxShadow
                            ? palette.avatarFallbackBoxShadow
                            : null,
                        border: border ? '1px solid ' + palette.greyMFC : null,
                    }}
                    data-testid={`Badge__UserBadge_${id || alt}`}
                >
                    <img
                        css={avatarImageStyles}
                        style={{
                            width: size || '100%',
                            height: size || '100%',
                            border: border
                                ? '1px solid ' + palette.greyMFC
                                : null,
                            // viewTransitionName: `avatar-${id}`,
                        }}
                        ref={imageRef}
                        src={avatarSrc}
                        alt={alt}
                        data-testid={
                            offline
                                ? `BadgeAvatar__Offline`
                                : `BadgeAvatar__Online`
                        }
                        crossOrigin="anonymous"
                    />
                </div>
                {!disableDrag && (
                    <DragHandle
                        ref={dragHandleRef}
                        size={size}
                        onDragStart={dragStart}
                        onDrag={dragMove}
                        onDragEnd={dragEnd}
                    />
                )}
                <Dot
                    css={getDotStyles(offline, broadcasting, disableBadge)}
                    data-testid="Badge__DotIcon"
                />
            </span>
        )
    }),
)

export default BadgeAvatar
